import { forwardRef, type ReactNode, useState, useRef } from "react"
import {
  TransformWrapper,
  type ReactZoomPanPinchRef
} from "react-zoom-pan-pinch"
import { ZoomableImageContent } from "../../../../components/common/imageMagnifier/components/ZoomableImageContent"
import {
  MAX_SCALE,
  MIN_SCALE,
  type PanDirection,
  getBoundedPosition,
  getCenteredZoomPosition,
  getNextScale,
  getPannedPosition,
  getTransformBounds
} from "./imageTransformHelpers"

const getTransformState = (transformApi: ReactZoomPanPinchRef) =>
  transformApi.instance?.transformState ?? transformApi.state

const getCurrentBounds = (
  transformApi: ReactZoomPanPinchRef,
  scale: number
) => {
  const { bounds, contentComponent, setup, wrapperComponent } =
    transformApi.instance

  if (!wrapperComponent || !contentComponent) return bounds

  return getTransformBounds({
    wrapperWidth: wrapperComponent.offsetWidth,
    wrapperHeight: wrapperComponent.offsetHeight,
    contentWidth: contentComponent.offsetWidth,
    contentHeight: contentComponent.offsetHeight,
    scale,
    centerZoomedOut: Boolean(setup?.centerZoomedOut)
  })
}

type ZoomImageContainerProps = {
  src?: string
  alt?: string
  className?: string
  tabIndex?: number
  children?: ReactNode
  isFullscreen: boolean
  setOpenFullImage: (status: boolean) => void
  iconColor?: string
  fullscreenButtonRef?: React.RefObject<HTMLButtonElement>
}

export const ZoomImageContainer = forwardRef<
  HTMLImageElement,
  ZoomImageContainerProps
>(({ isFullscreen, setOpenFullImage, fullscreenButtonRef, ...props }, ref) => {
  const [imageScale, setImageScale] = useState<number>(1)
  const containerRef = useRef<ReactZoomPanPinchRef | null>(null)

  const handleChangeFullscreen = (status: boolean) => {
    setOpenFullImage(status)
  }

  const handleZoomInOut =
    (decrease = false) =>
    () => {
      if (containerRef.current) {
        const { setTransform, instance } = containerRef.current
        const transformState = getTransformState(containerRef.current)
        if (!transformState) return

        const { scale = 1, positionY = 0, positionX = 0 } = transformState
        const newScale = getNextScale(scale, decrease)
        const centeredZoomPosition = getCenteredZoomPosition(
          { scale, positionX, positionY },
          {
            width: instance.wrapperComponent?.offsetWidth ?? 0,
            height: instance.wrapperComponent?.offsetHeight ?? 0
          },
          newScale
        )
        const { positionX: nextPositionX, positionY: nextPositionY } =
          getBoundedPosition(
            centeredZoomPosition,
            getCurrentBounds(containerRef.current, newScale)
          )

        setImageScale(newScale)
        setTransform(nextPositionX, nextPositionY, newScale, 0)
      }
    }

  const handleResetZoom = () => {
    if (containerRef.current) {
      const { setTransform } = containerRef.current
      const { positionX, positionY } = getBoundedPosition(
        { positionX: 0, positionY: 0 },
        getCurrentBounds(containerRef.current, MIN_SCALE)
      )

      setImageScale(1)
      setTransform(positionX, positionY, MIN_SCALE, 0)
    }
  }

  const handlePan = (direction: PanDirection) => () => {
    if (!containerRef.current) return

    const { setTransform } = containerRef.current
    const transformState = getTransformState(containerRef.current)
    if (!transformState || (transformState.scale ?? MIN_SCALE) <= MIN_SCALE) {
      return
    }

    const scale = transformState.scale ?? MIN_SCALE
    const { positionX, positionY } = getBoundedPosition(
      getPannedPosition(
        transformState.positionX ?? 0,
        transformState.positionY ?? 0,
        direction
      ),
      getCurrentBounds(containerRef.current, scale)
    )

    setTransform(positionX, positionY, scale, 0)
  }

  return (
    <TransformWrapper
      initialScale={1}
      maxScale={MAX_SCALE}
      minScale={MIN_SCALE}
      doubleClick={{ disabled: true }}
      wheel={{ disabled: true }}
      limitToBounds={true}
      centerOnInit={true}
      ref={containerRef}
    >
      <ZoomableImageContent
        ref={ref}
        onFullscreenChange={handleChangeFullscreen}
        isFullscreen={isFullscreen}
        fullscreenButtonRef={fullscreenButtonRef}
        imageScale={imageScale}
        handleZoomIn={handleZoomInOut(false)}
        handleZoomOut={handleZoomInOut(true)}
        handleResetZoom={handleResetZoom}
        canPan={imageScale > MIN_SCALE}
        handlePan={handlePan}
        {...props}
      />
    </TransformWrapper>
  )
})
